// Shared helpers for handling dropped files/folders. A dropped folder keeps its
// structure in S3 and is recorded as ONE logical asset (with parts[]). Used by
// the owner Uploader and the guest drop-upload page.

export const sanitizeSeg = (s) => s.replace(/[^\w.\-]+/g, '_');
export const sanitizePath = (p) => p.split('/').map(sanitizeSeg).filter(Boolean).join('/');

// Natural-ish sort so numbered parts (_001, _002…) order correctly.
export const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true });

// Recursively read a dropped DataTransfer entry into {file, relPath} items.
export async function readEntry(entry, prefix, out) {
  if (!entry) return;
  if (entry.isFile) {
    await new Promise((res) => {
      entry.file(
        (f) => {
          out.push({ file: f, relPath: prefix + entry.name });
          res();
        },
        () => res()
      );
    });
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    const childPrefix = `${prefix}${entry.name}/`;
    const readBatch = () =>
      new Promise((res) => reader.readEntries((e) => res(e || []), () => res([])));
    // readEntries returns in batches; loop until empty.
    // eslint-disable-next-line no-await-in-loop
    for (let batch = await readBatch(); batch.length; batch = await readBatch()) {
      for (const e of batch) {
        // eslint-disable-next-line no-await-in-loop
        await readEntry(e, childPrefix, out);
      }
    }
  }
}

// Read dropped DataTransfer items (files and/or folders) into {file, relPath}[].
export async function readDataTransfer(dt) {
  const handles = dt.items
    ? Array.from(dt.items)
        .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
        .filter(Boolean)
    : [];
  if (handles.length) {
    const out = [];
    for (const en of handles) {
      // eslint-disable-next-line no-await-in-loop
      await readEntry(en, '', out);
    }
    if (out.length) return out;
  }
  return dt.files?.length
    ? Array.from(dt.files).map((f) => ({ file: f, relPath: f.webkitRelativePath || f.name }))
    : [];
}

// Split {file, relPath}[] into top-level folder groups + loose files.
// Each group: { folderName, files:[{file, inner}], totalSize, primaryInner }
// where `inner` is the path inside the folder. Callers map these to S3 keys
// (owner builds keys directly; guest lets the backend build them under the
// drop prefix).
export function groupFolderEntries(entries) {
  const groupsByName = new Map();
  const loose = [];
  for (const { file, relPath } of entries) {
    const rel = relPath || file.name;
    const slash = rel.indexOf('/');
    if (slash > 0) {
      const folderName = rel.slice(0, slash);
      let g = groupsByName.get(folderName);
      if (!g) {
        g = { folderName, files: [], totalSize: 0 };
        groupsByName.set(folderName, g);
      }
      g.files.push({ file, inner: rel.slice(slash + 1) || file.name });
      g.totalSize += file.size;
    } else {
      loose.push({ file, relPath: rel });
    }
  }
  const groups = [...groupsByName.values()].map((g) => {
    g.files.sort((a, b) => byName(a.file, b.file));
    g.primaryInner = g.files[0].inner;
    return g;
  });
  return { groups, loose };
}
